import * as React from 'react';

import { useFloating } from '@floating-ui/react-dom';
import { translate } from '~l10n/client';
import cx from 'classnames';

import { PaywallScreens } from '~analytics/types';
import { IconButton } from '~shared/components/buttons';
import { Checkbox } from '~shared/components/Checkbox';
import { Divider } from '~shared/components/Divider';
import { FocusIndicator } from '~shared/components/FocusIndicator';
import { IconEllipsis } from '~shared/components/icons/standard';
import { Menu, MenuButton } from '~shared/components/Menu';
import { Portal } from '~shared/components/Portal';
import { useFocusWithin, useHover } from '~shared/hooks';
import { ReactNodeNoStrings } from '~shared/types';
import { PaywallDispatch } from '~teacher/components/PaywallDialog';

import { QuizListItemActions } from './QuizListItemActions';

export type QuizListItemBaseProps = {
  uniqueId: string;
  isChecked: boolean;
  toggleItem: (id: string) => void;
  disableHover?: boolean;
  closeSearchField?: () => void;
};

export interface QuizListItemProps extends QuizListItemBaseProps {
  children: React.ReactNode;
  itemIcon: React.ReactElement;
  href: string;
  showOwner?: boolean;
  date: Date;
  shortcutActions: ReactNodeNoStrings[];
  actionsMenu: React.ReactElement;
  closeSearchField?: () => void;
  locked?: boolean;
  paywallDispatch?: PaywallDispatch;
}

export function QuizListItem({
  children,
  uniqueId,
  isChecked,
  itemIcon,
  disableHover,
  href,
  showOwner,
  date,
  shortcutActions,
  actionsMenu,
  toggleItem,
  closeSearchField,
  locked,
  paywallDispatch,
}: QuizListItemProps) {
  const onSelectItem = () => toggleItem(uniqueId);
  const [hasFocus, setHasFocus] = React.useState(false);
  const { hoverProps, isHovered } = useHover({});
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (focus) => setHasFocus(focus),
  });

  const showCheckbox = hasFocus || isHovered || isChecked;

  const backgroundClass = cx(
    'absolute inset-0 -z-1',
    isChecked ? 'bg-cyan-50' : ''
  );

  const nameClass = cx(
    'group max-w-full text-base font-500 leading-8 text-blue-900 no-underline rounded-1 outline-none before:absolute before:inset-0 before:empty-content cursor-pointer',
    disableHover ?? 'hocus:text-cyan-700'
  );

  /**
   * Table is constructed with `div`s and aria-roles to avoid a bug with Safari
   * where `position:relative` can't be applied to `tr` elements
   */

  const { x, y, update, reference, floating } = useFloating({
    placement: 'bottom-end',
  });

  React.useEffect(() => {
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
    };
  }, [update]);

  const Component = locked ? 'div' : 'a';
  const clickHandler = locked
    ? () => {
        paywallDispatch({
          type: 'OPEN',
          touch: [PaywallScreens.QuizListAccessLocked],
        });
      }
    : () => {
        if (closeSearchField) {
          closeSearchField();
        }
      };

  return (
    <div
      role="row"
      data-tr
      className="relative flex items-center w-full"
      {...hoverProps}
      {...focusWithinProps}
    >
      <div role="cell" data-td className="w-16 p-0">
        <div className="relative z-10 flex justify-center py-5 pl-2">
          <div className="relative">
            <div className={`flex ${showCheckbox && 'opacity-0'}`}>
              {itemIcon}
            </div>
            <div
              className={`absolute inset-0 ml-1 ${
                showCheckbox ? '' : 'opacity-0'
              }`}
            >
              <Checkbox
                id={`checkbox-${uniqueId}`}
                aria-labelledby={`icon-title-${uniqueId} item-${uniqueId}`}
                size="large"
                checked={isChecked}
                onChange={onSelectItem}
                disabled={locked}
              />
            </div>
          </div>
        </div>
      </div>
      <div role="cell" data-td className="flex-1 w-full min-w-0 p-0">
        <div className="absolute right-0 bottom-0 left-0 z-0">
          <Divider />
        </div>
        <div className="flex flex-1 justify-between pl-2">
          <Component
            className={nameClass}
            id={`item-${uniqueId}`}
            onClick={clickHandler}
            href={locked ? '' : href}
          >
            <div className={backgroundClass} />
            <div className="relative px-2 py-1">
              <FocusIndicator />
              <div className="block truncate">{children}</div>
            </div>
          </Component>
        </div>
      </div>
      {showOwner ? (
        <div role="cell" data-td className="hidden md:block w-56 p-0">
          <div className="py-5 pl-4">
            <div className="text-base leading-8 text-blue-900">You</div>
          </div>
        </div>
      ) : null}
      <div role="cell" data-td className="hidden md:block w-52 p-0">
        <div className="py-5 px-4">
          <time
            className="text-base leading-8 text-blue-900"
            dateTime={date?.toString()}
          >
            {date.toLocaleDateString()}
          </time>
        </div>
      </div>
      <div role="cell" data-td className="w-12 p-0">
        <QuizListItemActions
          isChecked={isChecked}
          showShortcuts={hasFocus || isHovered}
          shortcuts={shortcutActions}
        >
          <Menu>
            <MenuButton ref={reference}>
              {({ props }) => (
                <IconButton aria-label={translate('More options')} {...props}>
                  <IconEllipsis />
                </IconButton>
              )}
            </MenuButton>
            <Portal>
              <div
                ref={floating}
                style={{
                  position: 'absolute',
                  top: y ?? '',
                  left: x ?? '',
                }}
              >
                {actionsMenu}
              </div>
            </Portal>
          </Menu>
        </QuizListItemActions>
      </div>
    </div>
  );
}
